import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { ReactComponent as AlertIcon } from '../../../assets/icons/ubr_alert.svg';
import { Input, Button, Img } from '../../../components';
import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/useAuth';
import { RoutesPaths } from '../../../routes';
import getValidationErrors from '../../../utils/getValidationErrors';
import UnicredLogo from './../../../assets/logos/logo_uni_horizontal_verde.svg';
import { Container, Wrapper, FormContent, ButtonContainer } from './style';

interface LoginFormData {
  usuario: string;
  senha: string;
}

export const Login: React.FC = () => {
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginRequestLoading, setLoginRequestLoading] = useState(false);
  const formRef = useRef<FormHandles | null>(null);
  const history = useHistory();
  const { addToast } = useToast();
  const { signIn, user } = useAuth();

  useEffect(() => {
    if (user) {
      history.push(RoutesPaths.home);
    }
  }, []);

  const delay = (amount = 750) =>
    new Promise(resolve => {
      setIsLoading(true);
      setTimeout(resolve, amount);
    });

  const handleSubmit = useCallback(
    async (data: LoginFormData) => {
      await delay(750);

      try {
        if (loginRequestLoading) {
          return;
        }

        setLoginRequestLoading(true);

        const { usuario, senha } = data;
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          usuario: Yup.string()
            .min(2, 'Curto demais')
            .required('Usuário obrigatório'),
          senha: Yup.string()
            .min(2, 'Curto demais')
            .required('Senha obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await signIn({ usuario, senha });

        setLoginRequestLoading(false);
        setIsLoading(false);

        history.push(RoutesPaths.home);
      } catch (error) {
        setLoginRequestLoading(false);
        setIsLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
          return;
        }
        setIsValid(true);

        addToast({
          type: 'error',
          title: 'Erro na autenticação',
          description: 'Ocorreu um erro ao fazer login, cheque as credenciais',
        });
      }
    },
    [signIn, addToast],
  );

  return (
    <Container>
      <Wrapper>
        <FormContent>
          <figure>
            <Img src={UnicredLogo} alt="Logo Unicred" />
          </figure>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input
              name="usuario"
              label="Usuário"
              type="text"
              data-testid={'user-field'}
            />
            <Input
              name="senha"
              label="Senha"
              type="password"
              data-testid={'password-field'}
            />
            <ButtonContainer>
              <Button
                variant={'solid'}
                type="submit"
                style={{
                  width: '100%',
                }}
                loading={isLoading}
                loadingMessage={'Carregando...'}
                data-testid={'submit-button'}
              >
                Entrar
              </Button>
              {isValid ? (
                <span>
                  <AlertIcon width={24} />
                  Usuário ou senha não encontrados.
                </span>
              ) : (
                ''
              )}
            </ButtonContainer>
          </Form>
        </FormContent>
      </Wrapper>
    </Container>
  );
};
